import { Resolution } from 'src/domains/calculators/utilities/types';
import {
  getCustomFramingDecisionValidation,
  type ContainerName,
} from './getCustomFramingDecisionValidation';

/**
 * Generic result of a validation check.
 * Components can use isValid to enable/disable UI and message for tooltips.
 */
export type ValidationResult = {
  isValid: boolean;
  message?: string;
};

/**
 * Combines multiple validation results. The overall result is valid only if all
 * are valid. When invalid, returns the first invalid result's message.
 */
export function combineValidationResults(
  results: ValidationResult[]
): ValidationResult {
  const firstInvalid = results.find((r) => !r.isValid);
  if (firstInvalid) {
    return {
      isValid: false,
      message: firstInvalid.message,
    };
  }
  return { isValid: true };
}

const FRAMING_INTENT_LABEL_NON_ASCII_MESSAGE =
  'Label cannot contain non-ASCII characters';
const FRAMING_INTENT_LABEL_TOO_LONG_MESSAGE =
  'Label cannot exceed 50 characters';

const FRAMING_INTENT_LABEL_MAX_LENGTH = 50;

/**
 * Validator for framing intent label value (non-ASCII and length rules).
 * Use this for both inline input validation and download validation.
 */
export function validateFramingIntentLabelValue(
  value: string
): ValidationResult {
  const hasNonAscii = /[^\x20-\x7E]/.test(value);
  const isTooLong = value.length > FRAMING_INTENT_LABEL_MAX_LENGTH;

  if (hasNonAscii) {
    return {
      isValid: false,
      message: FRAMING_INTENT_LABEL_NON_ASCII_MESSAGE,
    };
  }
  if (isTooLong) {
    return {
      isValid: false,
      message: FRAMING_INTENT_LABEL_TOO_LONG_MESSAGE,
    };
  }
  return { isValid: true };
}

/**
 * Validator for custom framing decision dimensions: must be > 0 and
 * within canvas / effective canvas. Use for download validation.
 */
export function validateCustomFramingWithinCanvas(
  customWidth: number | null | undefined,
  customHeight: number | null | undefined,
  containerResolution: Resolution | null | undefined,
  containerName: ContainerName = 'Canvas'
): ValidationResult {
  const result = getCustomFramingDecisionValidation(
    customWidth,
    customHeight,
    containerResolution,
    containerName
  );
  const message = result.widthWarning ?? result.heightWarning ?? undefined;
  if (message) {
    return { isValid: false, message };
  }
  return { isValid: true };
}

/**
 * Validator for effective canvas dimensions: when in use, must be > 0 and
 * within canvas. When both dimensions are null/undefined (effective canvas not
 * in use), returns valid. Use for download validation.
 */
export function validateEffectiveCanvasWithinCanvas(
  effectiveWidth: number | null | undefined,
  effectiveHeight: number | null | undefined,
  canvasResolution: Resolution | null | undefined
): ValidationResult {
  const notInUse = effectiveWidth == null && effectiveHeight == null;
  if (notInUse) {
    return { isValid: true };
  }
  return validateCustomFramingWithinCanvas(
    effectiveWidth,
    effectiveHeight,
    canvasResolution,
    'Canvas'
  );
}

/** Minimal form values needed for download validation */
export type FramingCalculatorDownloadValidationFormValues = {
  framingIntentLabel?: string | null;
  effectiveCanvasWidth?: number | null;
  effectiveCanvasHeight?: number | null;
  customFramingDecisionWidth?: number | null;
  customFramingDecisionHeight?: number | null;
};

/** Minimal framing chart props needed for download validation */
export type FramingCalculatorDownloadValidationChartProps = {
  canvasResolution: Resolution;
  effectiveCanvas: Resolution | null;
};

/**
 * Composes all validators for the Framing calculator download buttons.
 * Returns a single ValidationResult so Calculator does not need to know
 * individual validation steps or their order.
 */
export function getFramingCalculatorDownloadValidation(
  formValues: FramingCalculatorDownloadValidationFormValues,
  framingChartProps: FramingCalculatorDownloadValidationChartProps
): ValidationResult {
  const { canvasResolution, effectiveCanvas } = framingChartProps;
  const containerResolution = effectiveCanvas ?? canvasResolution;
  const containerName: ContainerName = effectiveCanvas
    ? 'Effective Canvas'
    : 'Canvas';

  return combineValidationResults([
    validateFramingIntentLabelValue(formValues.framingIntentLabel ?? ''),
    validateEffectiveCanvasWithinCanvas(
      formValues.effectiveCanvasWidth,
      formValues.effectiveCanvasHeight,
      canvasResolution
    ),
    validateCustomFramingWithinCanvas(
      formValues.customFramingDecisionWidth,
      formValues.customFramingDecisionHeight,
      containerResolution,
      containerName
    ),
  ]);
}
